import { Injectable, inject, signal } from '@angular/core';
import { ComposerStateService } from '@app/model-composer';

export type OptionalState = {
  enabled: boolean;
  quantity: number;
};

type DependencyType = 'and' | 'or';

@Injectable({ providedIn: 'root' })
export class ElementOptionalService {
  private readonly composerState = inject(ComposerStateService);
  private readonly _state = signal<Record<string, OptionalState>>({});
  readonly state = this._state.asReadonly();

  private key(uid: string, customizationId: string | number): string {
    return `${uid}::${customizationId}`;
  }

  get(uid: string, customizationId: string | number): OptionalState {
    return this._state()[this.key(uid, customizationId)] ?? { enabled: false, quantity: 1 };
  }

  set(uid: string, customizationId: string | number, value: OptionalState): void {
    const k = this.key(uid, customizationId);
    this._state.update(state => ({...state, [k]: value}));
  }

  getState(uid: string, customization: any): OptionalState {
    return this.get(uid, customization.id);
  }

  initializeDefaults(uid: string, customizationsInput: any): void {
    const customizations = this.normalizeCustomizations(customizationsInput);
    for (const customization of customizations) {
      if (customization?.type !== 'OPTIONAL') { continue; }
      const key = this.key(uid, customization.id);
      const exists = this._state()[key] != null;
      if (exists) { continue; }
      if (customization?.optional?.default?.quantity) {
        const defaultQty = Number(customization?.optional?.default?.quantity);
        this.set(uid, customization.id, {enabled: true, quantity: defaultQty});
      }
    }
  }

  maxQty(customization: any): number {
    return Number(customization?.optional?.maxQuantity ?? 1) || 1;
  }

  toggleOptional(uid: string, customization: any, customizationsInput: any): void {
    const customizations = this.normalizeCustomizations(customizationsInput);
    const currentState = this.get(uid, customization.id);
    const enabled = !currentState.enabled;
    const quantity = this.normalizeQty(customization, currentState.quantity ?? 1);
    this.setOptionalAndApply(uid, customization, enabled, quantity);
    if (!enabled) {
      this.disableChildOptionals(uid, customization, customizations, new Set<string>([String(customization.id)]));
      this.disableUnsatisfiedDependentOptionals(uid, customization, customizations, new Set<string>());
    }
    else {
      const alternativeCustomizationIds = this.alternativeCustomizationIds(customization);
      if (alternativeCustomizationIds) {

        this.disableAlternativeOptionals(uid, customization, customizations, new Set<string>([String(customization.id)]));


      }
    }
  }

  incQty(uid: string, customization: any, customizationsInput: any): void {
    const currentState = this.get(uid, customization.id);
    const quantity = this.normalizeQty(customization, (currentState.quantity ?? 1) + 1);
    this.setOptionalAndApply(uid, customization, true, quantity);
  }

  decQty(uid: string, customization: any, customizationsInput: any): void {
    const currentState = this.get(uid, customization.id);
    const quantity = this.normalizeQty(customization, (currentState.quantity ?? 1) - 1);
    this.setOptionalAndApply(uid, customization, true, quantity);
  }

  isCustomizationVisible(uid: string, customization: any, customizationsInput: any): boolean {
    const customizations = this.normalizeCustomizations(customizationsInput);
    return this.isCustomizationVisibleInternal(uid, customization, customizations, new Set<string>());
  }

  private normalizeCustomizations(customizationsInput: any): any[] {
    if (Array.isArray(customizationsInput)) {
      return customizationsInput;
    }
    return customizationsInput ? [customizationsInput] : [];
  }

  private optionalPlacements(customization: any): string[] {
    return customization?.optional?.default?.placements ?? customization?.optional?.placements ?? [];
  }

  private optionalPlacementsToRemove(customization: any): string[] {
    return customization?.optional?.default?.placementsToRemove ?? customization?.optional?.placementsToRemove ?? [];
  }

  private normalizeQty(customization: any, quantity: any): number {
    const max = this.maxQty(customization);
    const parsed = Number(quantity ?? 1);
    const safeQuantity = Number.isFinite(parsed) ? parsed : 1;
    return Math.min(Math.max(safeQuantity, 1), max);
  }

  private childCustomizationIds(customization: any): Array<string | number> {
    const ids = customization?.childCustomizationsIds ?? customization?.optional?.childCustomizationsIds ?? [];
    if (Array.isArray(ids)) {
      return ids.filter(id => id != null);
    }
    return ids != null ? [ids] : [];
  }

  private dependsOnCustomizationIds(customization: any): Array<string | number> {
    const ids =
      customization?.dependsOnCustomizationIds
      ?? customization?.optional?.dependsOnCustomizationIds
      ?? customization?.dependsOnCustomizationId
      ?? customization?.optional?.dependsOnCustomizationId
      ?? [];
    if (Array.isArray(ids)) {
      return ids.filter(id => id != null);
    }
    return ids != null ? [ids] : [];
  }

  private dependsOnCustomizationType(customization: any): DependencyType {
    const type = customization?.dependsOnCustomizationType ?? customization?.optional?.dependsOnCustomizationType ?? 'and';
    return type === 'or' ? 'or' : 'and';
  }

  private alternativeCustomizationIds(customization: any): Array<string | number> {
    const ids =
      customization?.alternativeCustomizationIds ?? [];
    if (Array.isArray(ids)) {
      return ids.filter(id => id != null);
    }
    return ids != null ? [ids] : [];
  }

  private isDependencySatisfied(uid: string, customization: any, customizations: any[], visited: Set<string>): boolean {
    const dependencyIds = this.dependsOnCustomizationIds(customization);
    if (!dependencyIds.length) { return true; }
    const dependencyType = this.dependsOnCustomizationType(customization);
    const checks = dependencyIds.map(dependencyId => {
      const parentCustomization = this.findCustomizationById(dependencyId, customizations);
      if (!parentCustomization || parentCustomization?.type !== 'OPTIONAL') { return false; }
      const parentState = this.get(uid, parentCustomization.id);
      return parentState.enabled && this.isCustomizationVisibleInternal(uid, parentCustomization, customizations, new Set(visited));
    });
    return dependencyType === 'or' ? checks.some(Boolean) : checks.every(Boolean);
  }

  private parentCustomizationsForChild(childId: string | number, customizations: any[]): any[] {
    const childKey = String(childId);
    return customizations.filter(customization => this.childCustomizationIds(customization).some(id => String(id) === childKey));
  }

  private dependentCustomizationsForParent(parentId: string | number, customizations: any[]): any[] {
    const parentKey = String(parentId);
    return customizations.filter(customization => this.dependsOnCustomizationIds(customization).some(id => String(id) === parentKey));
  }

  private setOptionalAndApply(uid: string, customization: any, enabled: boolean, quantity: any): void {
    const normalizedQuantity = this.normalizeQty(customization, quantity);
    this.set(uid, customization.id, { enabled, quantity: normalizedQuantity});
    this.composerState.applyOptionalToComposition({
      uid,
      optionalPlacements: this.optionalPlacements(customization),
      placementsToRemove: this.optionalPlacementsToRemove(customization),
      enabled,
      quantity: normalizedQuantity,
    });
  }

  private disableChildOptionals(uid: string, parentCustomization: any, customizations: any[], visited = new Set<string>()): void {
    for (const childId of this.childCustomizationIds(parentCustomization)) {
      const childKey = String(childId);
      if (visited.has(childKey)) { continue; }
      visited.add(childKey);
      const childCustomization = this.findCustomizationById(childId, customizations);
      if (!childCustomization) { continue; }
      this.disableChildOptionals(uid, childCustomization, customizations, visited);
      if (childCustomization?.type !== 'OPTIONAL') { continue; }
      const childState = this.get(uid, childCustomization.id);
      if (!childState.enabled) { continue; }
      this.setOptionalAndApply(uid, childCustomization, false, childState.quantity ?? 1);
    }
  }

  private disableAlternativeOptionals(uid: string, currentCustomization: any, customizations: any[], visited = new Set<string>()): void {
    for (const altId of this.alternativeCustomizationIds(currentCustomization)) {
      const altKey = String(altId);
      if (visited.has(altKey)) { continue; }
      visited.add(altKey);
      const altCustomization = this.findCustomizationById(altKey, customizations);
      if (!altCustomization) { continue; }
      this.disableAlternativeOptionals(uid, altCustomization, customizations, visited);
      if (altCustomization?.type !== 'OPTIONAL') { continue; }
      const altState = this.get(uid, altCustomization.id);
      if (!altState.enabled) { continue; }
      this.setOptionalAndApply(uid, altCustomization, false, altState.quantity ?? 1);
    }
  }

  private disableUnsatisfiedDependentOptionals(uid: string, parentCustomization: any, customizations: any[], visited = new Set<string>()): void {
    const parentKey = String(parentCustomization.id);
    if (visited.has(parentKey)) { return; }
    visited.add(parentKey);
    for (const dependentCustomization of this.dependentCustomizationsForParent(parentCustomization.id, customizations)) {
      const dependentKey = String(dependentCustomization.id);
      if (visited.has(dependentKey)) { continue; }
      if (dependentCustomization?.type !== 'OPTIONAL') { continue; }
      const dependentState = this.get(uid, dependentCustomization.id);
      if (!dependentState.enabled) { continue; }
      const isStillSatisfied = this.isDependencySatisfied(uid, dependentCustomization, customizations, new Set<string>());
      if (isStillSatisfied) { continue; }
      this.disableUnsatisfiedDependentOptionals(uid, dependentCustomization, customizations, new Set(visited));
      this.disableChildOptionals(uid, dependentCustomization, customizations, new Set(visited));
      this.setOptionalAndApply(uid, dependentCustomization, false, dependentState.quantity ?? 1);
    }
  }

  private isCustomizationVisibleInternal(uid: string, customization: any, customizations: any[], visited: Set<string>): boolean {
    if (!customization?.visible) { return false; }
    const customizationKey = String(customization.id);
    if (visited.has(customizationKey)) { return false; }
    visited.add(customizationKey);
    if (!this.isDependencySatisfied(uid, customization, customizations, visited)) { return false; }
    const parentCustomizations = this.parentCustomizationsForChild(customization.id, customizations);
    if (!parentCustomizations.length) { return true; }
    return parentCustomizations.some(parentCustomization => {
      if (parentCustomization?.type !== 'OPTIONAL') { return false; }
      const parentState = this.get(uid, parentCustomization.id);
      return parentState.enabled && this.isCustomizationVisibleInternal(uid, parentCustomization, customizations, new Set(visited));
    });
  }

  private findCustomizationById(id: string | number, customizations: any[]): any {
    const idKey = String(id);
    return customizations.find(customization => String(customization.id) === idKey);
  }
}
